import type { QueryClient } from '@tanstack/react-query'
import { getV1MeRetrieveQueryKey } from '@/api/client'
import type { RoleEnum } from '@/api/model'
import { sessionQuery, type Session } from '@/lib/auth'

type MeResponse = {
  status: number
  data: unknown
}

export type PanelAccessRedirect = '/login' | '/uczen' | null
export type StudentAccessRedirect = '/' | '/uczen/login' | null

const staffRoles = new Set<RoleEnum>(['superadmin', 'admin', 'teacher'])
const signedOutSession: Session = { isAuthenticated: false, user: null }

function successfulRole(response: MeResponse): RoleEnum | null {
  if (response.status !== 200 || !response.data || typeof response.data !== 'object') return null

  const role = (response.data as { role?: unknown }).role
  if (role === 'superadmin' || role === 'admin' || role === 'teacher' || role === 'student') {
    return role
  }
  return null
}

/**
 * Decide access before any panel child route can mount.
 *
 * `/me` is an authorization boundary, so an unexpected status or payload must
 * fail closed. Child routes may narrow staff roles further, but they can rely on
 * this parent decision having rejected unauthenticated and malformed responses.
 */
export function panelAccessRedirect(response: MeResponse): PanelAccessRedirect {
  const role = successfulRole(response)
  if (role === null) return '/login'
  if (role === 'student') return '/uczen'
  return staffRoles.has(role) ? null : '/login'
}

/** Fail-closed counterpart for the student web shell. */
export function studentAccessRedirect(response: MeResponse): StudentAccessRedirect {
  const role = successfulRole(response)
  if (role === null) return '/uczen/login'
  return role === 'student' ? null : '/'
}

/**
 * Keep the two auth caches consistent when the authoritative `/me` boundary
 * rejects a previously cached browser session. Without this reset, `/login`
 * can immediately redirect back to the panel from a fresh cached session while
 * the cached `/me` 401 redirects to login again.
 */
export function resetRejectedMeSession(queryClient: QueryClient): void {
  queryClient.setQueryData(sessionQuery.queryKey, signedOutSession)
  queryClient.removeQueries({ queryKey: getV1MeRetrieveQueryKey(), exact: true })
}
